import {
  ZadCatalogueCategory,
  ZadCatalogueProductInfo,
  ZadCatalogueProductManagement,
  ZadCatalogueProductType,
} from '../../../models/zad'
import {getFilePath} from '../../../helpers/file-helpers'
import {resolveZadUserMessage, ZadApiError} from './errors'

export const ZAD_CATALOGUE_IMAGE_MAX_BYTES = 5 * 1024 * 1024
export const ZAD_CATALOGUE_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
export const ZAD_CATALOGUE_IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.webp'] as const
export const ZAD_CATALOGUE_DESCRIPTION_MAX = 2000

export function asCatalogueString(value: unknown): string {
  if (value == null) return ''
  return String(value)
}

function asOptionalString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined
}

function asCatalogueId(value: unknown): string | undefined {
  if (typeof value === 'string' && value.trim()) return value.trim()
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  if (value && typeof value === 'object') {
    const raw = value as {_id?: unknown; id?: unknown; $oid?: unknown}
    if (typeof raw.$oid === 'string' && raw.$oid.trim()) return raw.$oid.trim()
    if (typeof raw._id === 'string' && raw._id.trim()) return raw._id.trim()
    if (typeof raw.id === 'string' && raw.id.trim()) return raw.id.trim()
  }
  return undefined
}

function asPublishedFlag(value: unknown): boolean {
  return value === true || value === 'true' || value === 1
}

function sanitizeCategory(value: unknown): ZadCatalogueCategory | undefined {
  if (!value || typeof value !== 'object') return undefined
  const raw = value as Record<string, unknown>
  const name = asCatalogueString(raw.name)
  const code = asCatalogueString(raw.code)
  if (!name && !code) return undefined
  return {
    _id: asOptionalString(raw._id),
    name,
    code,
  }
}

function sanitizeProductType(value: unknown): ZadCatalogueProductType | undefined {
  if (!value) return undefined
  if (typeof value === 'string') return undefined
  if (typeof value !== 'object') return undefined
  const raw = value as Record<string, unknown>
  return {
    _id: asOptionalString(raw._id),
    name: asCatalogueString(raw.name),
    code: asCatalogueString(raw.code),
    category: sanitizeCategory(raw.category) || (typeof raw.category === 'string' ? raw.category : undefined),
  }
}

export function sanitizeCatalogueProductInfo(value: unknown): ZadCatalogueProductInfo | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const id = asCatalogueId(raw._id) || asCatalogueId(raw.id)
  if (!id) return null
  const salePrice = typeof raw.salePrice === 'number' ? raw.salePrice : Number(raw.salePrice)
  return {
    _id: id,
    name: asCatalogueString(raw.name),
    code: asCatalogueString(raw.code),
    barcode: raw.barcode == null ? null : asCatalogueString(raw.barcode),
    salePrice: Number.isFinite(salePrice) ? salePrice : 0,
    brand: asOptionalString(raw.brand),
    publisher: asOptionalString(raw.publisher),
    isActive: raw.isActive !== false,
    productType: sanitizeProductType(raw.productType) || (typeof raw.productType === 'string' ? raw.productType : undefined),
  }
}

export function sanitizeCatalogueItem(value: unknown): ZadCatalogueProductManagement | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  const id = asCatalogueId(raw._id) || asCatalogueId(raw.id)
  const product = sanitizeCatalogueProductInfo(raw.product)
  if (!id || !product) return null
  const displayOrder = typeof raw.displayOrder === 'number' ? raw.displayOrder : Number(raw.displayOrder)
  return {
    _id: id,
    product,
    isPublished: asPublishedFlag(raw.isPublished),
    isFeatured: raw.isFeatured === true,
    publicDescription: asCatalogueString(raw.publicDescription),
    displayOrder: Number.isFinite(displayOrder) && displayOrder >= 0 ? displayOrder : 0,
    imageUrl: raw.imageUrl == null || raw.imageUrl === '' ? null : asCatalogueString(raw.imageUrl),
  }
}

export function extractCatalogueList(data: unknown): {
  items: ZadCatalogueProductManagement[]
  total: number
  totalPages: number
} {
  const raw = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const source = raw.catalogue || raw.catalogueProducts || raw.items || raw.products
  const items = Array.isArray(source)
    ? source.map(sanitizeCatalogueItem).filter((item): item is ZadCatalogueProductManagement => Boolean(item))
    : []
  return {
    items,
    total: typeof raw.total === 'number' ? raw.total : items.length,
    totalPages: typeof raw.totalPages === 'number' ? raw.totalPages : 1,
  }
}

export function extractCatalogueItem(data: unknown): ZadCatalogueProductManagement | null {
  if (!data || typeof data !== 'object') return null
  const raw = data as Record<string, unknown>
  return (
    sanitizeCatalogueItem(raw.catalogue) ||
    sanitizeCatalogueItem(raw.catalogueProduct) ||
    sanitizeCatalogueItem(raw.item) ||
    sanitizeCatalogueItem(raw)
  )
}

export function getCatalogueProductId(item: ZadCatalogueProductManagement | null | undefined): string {
  return item?.product?._id || ''
}

export function getCatalogueCategoryLabel(item: ZadCatalogueProductManagement | null | undefined): string {
  const type = item?.product?.productType
  if (!type || typeof type === 'string') return ''
  const category = type.category
  if (!category || typeof category === 'string') return ''
  return category.name || ''
}

export function getCatalogueTypeLabel(item: ZadCatalogueProductManagement | null | undefined): string {
  const type = item?.product?.productType
  if (!type || typeof type === 'string') return ''
  return type.name || ''
}

export function getCatalogueCategoryId(item: ZadCatalogueProductManagement | null | undefined): string {
  const type = item?.product?.productType
  if (!type || typeof type === 'string') return ''
  const category = type.category
  if (!category) return ''
  if (typeof category === 'string') return category
  return category._id ? String(category._id) : ''
}

export function resolveCatalogueImageUrl(imageUrl?: string | null): string {
  return getFilePath(imageUrl)
}

export function canPublishCatalogueProduct(item: ZadCatalogueProductManagement | null | undefined): boolean {
  return Boolean(item?.product && item.product.isActive !== false)
}

function imageExtensionAllowed(fileName: string): boolean {
  const name = String(fileName || '').toLowerCase()
  return ZAD_CATALOGUE_IMAGE_EXTENSIONS.some((ext) => name.endsWith(ext))
}

export function validateZadCatalogueImageFile(file?: File | null): string | null {
  if (!file) return 'ZAD.CATALOGUE.IMAGE_REQUIRED'
  const type = String(file.type || '').toLowerCase()
  if (type === 'application/pdf' || type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg') || file.name.toLowerCase().endsWith('.pdf')) {
    return 'ZAD.CATALOGUE.IMAGE_TYPE_INVALID'
  }
  const typeOk =
    ZAD_CATALOGUE_IMAGE_MIME_TYPES.includes(type as (typeof ZAD_CATALOGUE_IMAGE_MIME_TYPES)[number]) ||
    (!file.type && imageExtensionAllowed(file.name))
  if (!typeOk) return 'ZAD.CATALOGUE.IMAGE_TYPE_INVALID'
  if (file.size > ZAD_CATALOGUE_IMAGE_MAX_BYTES) return 'ZAD.CATALOGUE.IMAGE_TOO_LARGE'
  return null
}

export function formatCatalogueFileSize(size: number): string {
  if (!Number.isFinite(size) || size <= 0) return '0 KB'
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function clampCatalogueDescription(value: string): string {
  return String(value || '').slice(0, ZAD_CATALOGUE_DESCRIPTION_MAX)
}

export function sanitizeCatalogueWritePayload(values: {
  publicDescription?: string
  isFeatured?: boolean
  displayOrder?: number
}) {
  const displayOrder = Number(values.displayOrder)
  return {
    publicDescription: clampCatalogueDescription(asCatalogueString(values.publicDescription)),
    isFeatured: values.isFeatured === true,
    displayOrder: Number.isFinite(displayOrder) && displayOrder >= 0 ? Math.floor(displayOrder) : 0,
  }
}

export function resolveZadCatalogueMessage(err: ZadApiError): {i18nId: string; fallback: string} {
  const map: Record<string, string> = {
    PRODUCT_INACTIVE: 'ZAD.CATALOGUE.PRODUCT_INACTIVE',
    PRODUCT_NOT_FOUND: 'ZAD.PRODUCTS.NOT_FOUND',
    CATALOGUE_ALREADY_EXISTS: 'ZAD.CATALOGUE.ALREADY_EXISTS',
    CATALOGUE_PRODUCT_ALREADY_EXISTS: 'ZAD.CATALOGUE.ALREADY_EXISTS',
    CATALOGUE_NOT_FOUND: 'ZAD.CATALOGUE.NOT_FOUND',
    CATALOGUE_PRODUCT_NOT_FOUND: 'ZAD.CATALOGUE.NOT_FOUND',
    DUPLICATE: 'ZAD.CATALOGUE.ALREADY_EXISTS',
    INVALID_CATALOGUE_STATUS: 'ZAD.CATALOGUE.VALIDATION_ERROR',
    FORBIDDEN_CATALOGUE_FIELD: 'ZAD.CATALOGUE.VALIDATION_ERROR',
    INVALID_PUBLIC_DESCRIPTION: 'ZAD.CATALOGUE.VALIDATION_ERROR',
    INVALID_DISPLAY_ORDER: 'ZAD.CATALOGUE.VALIDATION_ERROR',
    INVALID_FILE_TYPE: 'ZAD.CATALOGUE.IMAGE_TYPE_INVALID',
    INVALID_IMAGE_TYPE: 'ZAD.CATALOGUE.IMAGE_TYPE_INVALID',
    FILE_TOO_LARGE: 'ZAD.CATALOGUE.IMAGE_TOO_LARGE',
    IMAGE_TOO_LARGE: 'ZAD.CATALOGUE.IMAGE_TOO_LARGE',
    VALIDATION_ERROR: 'ZAD.CATALOGUE.VALIDATION_ERROR',
    ROLE_FORBIDDEN: 'ZAD.COMMON.FORBIDDEN',
  }
  if (err.errorCode && map[err.errorCode]) {
    return {i18nId: map[err.errorCode], fallback: err.error}
  }
  return resolveZadUserMessage(err)
}
